import { useState } from "react";
import { LayoutTemplate, Trash2, Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { uid } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { TemplateMetaDialog } from "./TemplateMetaDialog";

const NONE = "Uncategorised";

export const TemplatePicker = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const openIt = () => { setOpen(true); setCat(""); setQ(""); api.get("/design-templates").then((r) => setList(r.data)); };
  const pick = (t) => {
    onPick({ id: uid(), name: t.name, scale: t.scale, image: t.image, shapes: t.shapes.map((s) => ({ ...s, id: uid(), dims: { ...s.dims } })) });
    toast.success(`"${t.name}" added to quote`);
    setOpen(false);
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/design-templates/${t.id}`); setList((l) => l.filter((x) => x.id !== t.id)); toast.success("Template deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const cats = [...new Set((list || []).map((t) => t.category || NONE))];
  const activeCat = cats.includes(cat) ? cat : "";
  const shown = (list || []).filter((t) => (!activeCat || (t.category || NONE) === activeCat) && (!q || `${t.name} ${t.category}`.toLowerCase().includes(q.toLowerCase())));
  const groups = shown.reduce((g, t) => ({ ...g, [t.category || NONE]: [...(g[t.category || NONE] || []), t] }), {});

  return (
    <>
      <Button data-testid="from-template-button" variant="outline" onClick={openIt}><LayoutTemplate className="mr-1 h-4 w-4" />From template</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="template-picker-dialog" className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Design templates</DialogTitle></DialogHeader>
          {list && !list.length && <p className="py-8 text-center text-sm text-zinc-400">No templates yet. Use the bookmark icon on a design card to save one.</p>}
          {!!list?.length && (
            <div className="space-y-3">
              <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" /><Input data-testid="template-search-input" placeholder="Search templates…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" /></div>
              <div className="flex flex-wrap gap-1.5">
                {["", ...cats].map((c) => (
                  <button key={c || "all"} data-testid={`template-filter-${c || "all"}`} onClick={() => setCat(c)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${activeCat === c ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 ring-1 ring-zinc-200 hover:ring-zinc-400"}`}>
                    {c || "All"} <span className="opacity-60">{c ? list.filter((t) => (t.category || NONE) === c).length : list.length}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {Object.entries(groups).map(([g, items]) => (
            <div key={g} data-testid={`template-group-${g}`}>
              <div className="eyebrow mb-2 mt-2">{g}</div>
              <div className="grid gap-3 sm:grid-cols-3">
                {items.map((t) => (
                  <div key={t.id} data-testid={`template-card-${t.id}`} className="group relative overflow-hidden rounded-lg border border-zinc-200 transition-colors hover:border-red-500">
                    <button data-testid={`template-use-${t.id}`} onClick={() => pick(t)} className="block w-full text-left">
                      <div className="flex h-32 items-center justify-center overflow-hidden bg-slate-50 p-2">{t.image ? <img src={t.image} alt={t.name} className="max-h-full max-w-full object-contain" /> : <LayoutTemplate className="h-8 w-8 text-zinc-300" />}</div>
                      <div className="p-2.5"><div className="truncate text-sm font-semibold">{t.name}</div><div className="text-xs text-zinc-400">{t.shapes.length} shapes · click to add</div></div>
                    </button>
                    <div className="absolute right-1.5 top-1.5 flex gap-1 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                      <button data-testid={`template-edit-${t.id}`} onClick={() => setEditing(t)} className="rounded bg-white/90 p-1.5 text-zinc-400 shadow-sm hover:text-zinc-900"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`template-delete-${t.id}`} onClick={() => remove(t)} className="rounded bg-white/90 p-1.5 text-zinc-400 shadow-sm hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!!list?.length && !shown.length && <p className="py-6 text-center text-sm text-zinc-400">No templates match.</p>}
        </DialogContent>
      </Dialog>
      <TemplateMetaDialog template={editing} onClose={() => setEditing(null)} onSaved={(t) => setList((l) => l.map((x) => (x.id === t.id ? t : x)))} />
    </>
  );
};
